// ============================================================================
//  SearchSelect — a dropdown you can type into
// ----------------------------------------------------------------------------
//  A native <select> is fine for five options and useless for three hundred: the
//  procedure catalogue alone offers 296 for one scheme, and the diagnosis
//  catalogue is 829. Scrolling to find "Malaria" is not a workflow.
//
//  So: the closed control shows what is picked; opening it puts the cursor in a
//  search box, and typing narrows the list as you go.
//
//    · matches CODE or NAME, case-insensitively
//    · every word must match, in any order — "cast band" finds "Bandaging /
//      Cast Application", which is what a claims officer half-remembering a
//      procedure actually types
//    · exact code first, then name-start, then the rest, so typing "OPDC06A"
//      or "malaria" puts the obvious answer at the top
//    · arrows move, Enter picks, Escape closes, clicking outside closes
//
//  Long lists are CAPPED at `renderLimit` rows with a "keep typing" line rather
//  than rendered whole — 829 DOM nodes per keystroke is what makes a picker feel
//  broken. The cap is on what is DRAWN, never on what is searched.
// ============================================================================
import React, { useEffect, useMemo, useRef, useState } from 'react';
import { Search, ChevronDown, X } from 'lucide-react';
import { matchOptions } from './searchMatch';

const C = {
  green: '#1A9B7F', greenDark: '#137a63', greenSoft: '#e8f5f1',
  line: '#e2e8f0', ink: '#0f172a', inkSoft: '#475569', inkFaint: '#94a3b8',
  surface: '#fff', hover: '#f1f5f9',
};


export default function SearchSelect({
  options = [],
  value = '',
  onChange,
  placeholder = 'Select',
  searchPlaceholder = 'Type to search…',
  disabled,
  ariaLabel,
  renderLimit = 60,
  // Rendered under each row when present, e.g. a specialty or a tariff.
  secondaryOf,
  //  The main line of each LIST ROW. Defaults to "CODE — Name", which suits a
  //  procedure list where the code is the thing you know. A diagnosis list is the
  //  other way round: the same ICD-10 appears under several G-DRGs, so leading
  //  with the code makes five real choices look like one repeated five times.
  primaryOf,
  //  What the CLOSED control shows — a different question from what a list row
  //  shows. The control sits in a table column with a heading, and that heading is
  //  a promise: a field under ICD10 must read back the ICD-10, not the diagnosis
  //  name, even when the list is best browsed by name. Falls back to primaryOf,
  //  then to the default, so a caller that does not care need not say.
  triggerOf,
  //  What the CLOSED control shows once something is chosen. Defaults to the same
  //  "CODE — Name" as a list row, which is right when this is the only place the
  //  choice appears — and wrong in a table that gives the name a column of its
  //  own, where the cell should hold just the code.
  triggerLabelOf,
  //  Identity. Defaults to the code, but a catalogue whose rows are only
  //  distinguishable as a PAIR (ICD-10 + G-DRG) has to say so, or selecting one
  //  row silently selects a different one.
  idOf = (o) => o.code,
  emptyText = 'Nothing matches',
  //  Open, and put the cursor in the search box, the moment this mounts.
  //
  //  For the picker a row is ADDED to be filled in with. Clicking "+ Add" already
  //  says what you are about to do; making you then click the closed control and
  //  click again into its search box is two clicks to reach a state the Add
  //  implied. On a month of claims that is thousands of clicks buying nothing.
  //
  //  MOUNT ONLY, deliberately: the effect below has an empty dependency list, so a
  //  re-render of a row you have already dealt with cannot re-open its list and
  //  steal the cursor back from wherever you have moved it.
  autoOpen = false,
}) {
  const [open, setOpen] = useState(false);
  const [query, setQuery] = useState('');
  const [active, setActive] = useState(0);
  //  Where to draw the panel. It is position:FIXED, measured from the trigger,
  //  because every ancestor between here and the page is a scrollport: the table
  //  wrapper and the fold body both carry overflow-x:auto, and an absolutely
  //  positioned panel would be clipped by them — the dropdown would open and
  //  show three rows inside the cell.
  const [box, setBox] = useState(null);
  const rootRef = useRef(null);
  const inputRef = useRef(null);
  const listRef = useRef(null);

  const selected = useMemo(
    () => options.find((o) => String(idOf(o)) === String(value)) || null,
    [options, value, idOf],
  );

  //  Filtering and ranking live in searchMatch.js so they can be executed by the
  //  test suite rather than asserted against this file's source.
  const matches = useMemo(() => matchOptions(options, query), [options, query]);

  const shown = matches.slice(0, renderLimit);
  const hidden = matches.length - shown.length;

  // A new query invalidates the highlighted row.
  useEffect(() => { setActive(0); }, [query]);

  //  A row added to be filled in opens itself. The focus follows for free — the
  //  effect below already puts the cursor in the search box whenever `open`
  //  becomes true, so there is no second focus call to keep in step with it.
  //
  //  Empty deps: this fires once, when the row mounts. See the prop's note.
  useEffect(() => {
    if (autoOpen && !disabled) setOpen(true);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // ── Focus the search box as soon as it opens ──────────────────────────────
  //  The point of this control is to type immediately, and it has never actually
  //  done that — including when opened by clicking the trigger.
  //
  //  WHY IT DIDN'T. The panel renders on `open && box`. `box` is measured by the
  //  placement effect below, which also runs on `open`. So on the render where
  //  `open` first becomes true, `box` is still null, the panel is not in the DOM
  //  and `inputRef.current` is null — the focus call was skipped. The placement
  //  effect then set `box`, the panel appeared, and this effect never ran again
  //  because `open` had not changed. The cursor was left wherever it was and you
  //  had to click into the box by hand.
  //
  //  So it waits for the panel to EXIST, not merely for the intent to open. The
  //  ref makes it fire once per open cycle rather than on every `box` change —
  //  `box` is recomputed on scroll, and re-focusing the input as the page scrolls
  //  under it would fight anyone who had moved on.
  const focusedForOpen = useRef(false);
  useEffect(() => {
    if (!open) { setQuery(''); focusedForOpen.current = false; return; }
    if (box && inputRef.current && !focusedForOpen.current) {
      inputRef.current.focus();
      focusedForOpen.current = true;
    }
  }, [open, box]);

  // Click anywhere else closes it.
  useEffect(() => {
    if (!open) return undefined;
    const onDown = (e) => {
      if (rootRef.current && !rootRef.current.contains(e.target)) setOpen(false);
    };
    document.addEventListener('mousedown', onDown);
    return () => document.removeEventListener('mousedown', onDown);
  }, [open]);

  //  Measure the trigger and follow it. The capture phase is what makes this work
  //  through the table's and the fold's own scrollports — a bubbling listener on
  //  window never sees an inner element scroll.
  useEffect(() => {
    if (!open) { setBox(null); return undefined; }
    const place = () => {
      const el = rootRef.current;
      if (!el) return;
      const r = el.getBoundingClientRect();
      const below = window.innerHeight - r.bottom;
      //  A hard cap, not "as tall as there is room for". Without it a trigger low
      //  on the page opened a 680px panel that reached the top of the window and
      //  covered the page header and the claim's own sticky bar — which is what
      //  made it feel wrong rather than merely tall.
      const CAP = 320;
      // Flip above only when the panel genuinely does not fit below AND there is
      // more room above, so a row near the bottom still shows its list.
      const up = below < CAP && r.top > below;
      const room = (up ? r.top : below) - 12;
      setBox({
        left: r.left,
        top: up ? null : r.bottom + 3,
        bottom: up ? window.innerHeight - r.top + 3 : null,
        width: r.width,
        maxHeight: Math.max(180, Math.min(CAP, room)),
      });
    };
    place();
    window.addEventListener('scroll', place, true);
    window.addEventListener('resize', place);
    return () => {
      window.removeEventListener('scroll', place, true);
      window.removeEventListener('resize', place);
    };
  }, [open]);

  // Keep the highlighted row in view while arrowing through a long list.
  useEffect(() => {
    if (!open || !listRef.current) return;
    const el = listRef.current.querySelector('[data-active="1"]');
    if (el && el.scrollIntoView) el.scrollIntoView({ block: 'nearest' });
  }, [active, open]);

  //  Hands back the IDENTITY and the whole option. The caller needs the option
  //  when the identity is a composite — the row has to store the ICD-10 and the
  //  G-DRG separately, and neither can be recovered from "N80::OBGY01" by anyone
  //  who does not already know how it was built.
  const pick = (opt) => {
    if (!opt) return;
    if (onChange) onChange(idOf(opt), opt);
    setOpen(false);
  };

  const onKeyDown = (e) => {
    if (e.key === 'ArrowDown' || e.key === 'ArrowUp') {
      e.preventDefault();
      if (!open) { setOpen(true); return; }
      const step = e.key === 'ArrowDown' ? 1 : -1;
      setActive((a) => Math.min(shown.length - 1, Math.max(0, a + step)));
    } else if (e.key === 'Enter') {
      if (open) { e.preventDefault(); pick(shown[active]); }
    } else if (e.key === 'Escape') {
      if (open) { e.preventDefault(); setOpen(false); }
    }
  };

  const trigger = {
    display: 'flex', alignItems: 'center', gap: 6, width: '100%',
    boxSizing: 'border-box', padding: '6px 8px', fontSize: 12, textAlign: 'left',
    border: `1px solid ${disabled ? C.line : C.green}`, borderRadius: 7,
    background: disabled ? '#f8fafc' : C.surface,
    color: selected ? C.ink : C.inkFaint,
    cursor: disabled ? 'default' : 'pointer', font: 'inherit', fontFamily: 'inherit',
  };

  return (
    <div ref={rootRef} style={{ position: 'relative' }} onKeyDown={onKeyDown}>
      <button type="button" style={trigger} disabled={disabled}
        aria-haspopup="listbox" aria-expanded={open} aria-label={ariaLabel}
        onClick={() => !disabled && setOpen((o) => !o)}>
        <span style={{
          flex: 1, minWidth: 0, overflow: 'hidden', textOverflow: 'ellipsis',
          whiteSpace: 'nowrap',
        }}>
          {selected
            ? (triggerOf ? triggerOf(selected)
              : (primaryOf ? primaryOf(selected) : <>
                  <strong style={{ fontWeight: 800 }}>{selected.code}</strong>
                  {selected.name ? ` — ${selected.name}` : ''}
                </>))
            : placeholder}
        </span>
        <ChevronDown size={13} style={{ color: C.inkFaint, flexShrink: 0 }} />
      </button>

      {open && box && (
        <div style={{
          position: 'fixed', zIndex: 60,
          left: box.left,
          ...(box.top != null ? { top: box.top } : { bottom: box.bottom }),
          minWidth: box.width, width: Math.max(360, box.width), maxWidth: '92vw',
          display: 'flex', flexDirection: 'column',
          maxHeight: box.maxHeight,
          background: C.surface, border: `1px solid ${C.line}`, borderRadius: 9,
          boxShadow: '0 12px 30px rgba(15,23,42,.16)', overflow: 'hidden',
        }}>
          <div style={{
            display: 'flex', alignItems: 'center', gap: 7, padding: '8px 9px',
            borderBottom: `1px solid ${C.line}`, background: '#f8fafc', flexShrink: 0,
          }}>
            <Search size={13} style={{ color: C.inkFaint, flexShrink: 0 }} />
            <input
              ref={inputRef}
              value={query}
              onChange={(e) => setQuery(e.target.value)}
              placeholder={searchPlaceholder}
              aria-label={`Search ${ariaLabel || 'options'}`}
              style={{
                flex: 1, minWidth: 0, border: 0, outline: 'none', fontSize: 12,
                background: 'transparent', color: C.ink, font: 'inherit',
                fontFamily: 'inherit',
              }} />
            {query && (
              <button type="button" aria-label="Clear search"
                onClick={() => { setQuery(''); if (inputRef.current) inputRef.current.focus(); }}
                style={{ border: 0, background: 'none', cursor: 'pointer',
                  color: C.inkFaint, padding: 2, display: 'flex' }}>
                <X size={12} />
              </button>
            )}
            <span style={{ fontSize: 10, color: C.inkFaint, whiteSpace: 'nowrap' }}>
              {matches.length}
            </span>
          </div>

          <div ref={listRef} role="listbox" aria-label={ariaLabel}
            style={{ flex: 1, minHeight: 0, overflowY: 'auto' }}>
            {shown.length === 0 && (
              <div style={{ padding: '16px 12px', fontSize: 12, color: C.inkFaint,
                textAlign: 'center' }}>{emptyText}</div>
            )}
            {shown.map((o, i) => {
              const isActive = i === active;
              const isSelected = String(idOf(o)) === String(value);
              const secondary = secondaryOf ? secondaryOf(o) : null;
              return (
                <div
                  key={`${idOf(o)}-${i}`}
                  role="option"
                  aria-selected={isSelected}
                  data-active={isActive ? '1' : '0'}
                  onMouseEnter={() => setActive(i)}
                  onMouseDown={(e) => { e.preventDefault(); pick(o); }}
                  style={{
                    padding: '7px 11px', cursor: 'pointer', fontSize: 12,
                    background: isActive ? C.greenSoft : 'transparent',
                    borderLeft: `3px solid ${isSelected ? C.green : 'transparent'}`,
                  }}>
                  <div style={{ color: C.ink }}>
                    {primaryOf ? primaryOf(o) : <>
                      <strong style={{ fontWeight: 800, color: C.greenDark }}>{o.code}</strong>
                      {o.name ? ` — ${o.name}` : ''}
                    </>}
                  </div>
                  {secondary && (
                    <div style={{ fontSize: 10.5, color: C.inkFaint, marginTop: 1 }}>
                      {secondary}
                    </div>
                  )}
                </div>
              );
            })}
            {hidden > 0 && (
              // Never silently truncated: the count says what is not on screen.
              <div style={{ padding: '8px 11px', fontSize: 10.5, color: C.inkFaint,
                borderTop: `1px solid ${C.line}`, background: '#f8fafc' }}>
                {hidden} more — keep typing to narrow it down
              </div>
            )}
          </div>
        </div>
      )}
    </div>
  );
}
